import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppSelector } from '../../store';
import { Nada, CreateNadaRequest, TableColumn, SimplePaginationInfo } from '../../types';
import DataTable from '../../common/components/DataTable';
import { mastersService } from '../../services/mastersService';
import { ValuesetParam } from '../../common/utils/valueset';
import { toast } from '../../common/utils/toast';

const AddNada: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [nadaList, setNadaList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingNada, setEditingNada] = useState<Nada | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Validation schema
  const validationSchema = yup.object({
    name: yup.string().required('Nada Name is required').min(2, 'Nada Name must be at least 2 characters'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CreateNadaRequest>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
    },
  });

  // Debug form state

  const loadNada = async () => {
    try {
      setLoading(true);
      const { data } = await mastersService.getValuesByParam(ValuesetParam.NADA);
      setNadaList(Array.isArray(data) ? data : []);
    } catch (_) {
      setNadaList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNada();
  }, []);

  const handleEditClick = (row: any) => {
    const name = row?.valuesetnameen || row?.valuesetnameml || row?.valuesetcode || '';
    setValue('name', name);
    setIsEditMode(true);
    setEditingNada(row);
  };

  const handleReset = () => {
    reset({ name: '' });
    setIsEditMode(false);
    setEditingNada(null);
  };

  const onSubmit = async (data: CreateNadaRequest) => {
    try {
      if (isEditMode && editingNada) {
        // For edit mode, use tblid from row data
        const tblid = (editingNada as any).tblid;
        if (!tblid) {
          toast.showError('No tblid found in editing nada');
          return;
        }
        
        // Construct update payload with correct property names
        const updatePayload = {
          valuesetNameEn: data.name,
          valuesetNameMl: data.name,
          modifiedby: user?.userid || 'SYSTEM'
        };
        
        await mastersService.updateValueset(tblid, updatePayload);
        toast.showSuccess('Nada updated successfully!');
      } else {
        // Create new NADA using valueset API
        const createPayload = buildCreatePayload(data.name);
        await mastersService.createValueset(createPayload);
        toast.showSuccess('Nada created successfully!');
      }
      handleReset();
      loadNada();
    } catch (err: any) {
      toast.showError(err?.message || 'Failed to save nada');
    }
  };

  const buildCreatePayload = (nadaName: string) => {
    
    // Get the highest valuesetOrder from current data
    const maxOrder = nadaList.length > 0 
      ? Math.max(...nadaList.map(item => item.valuesetorder || 0)) + 1 
      : 1;

    // Get the highest valuesetCode and increment the last number
    let newValuesetCode = 'L1100L23L1'; // Default if no data
    if (nadaList.length > 0) {
      const sortedCodes = nadaList
        .map(item => item.valuesetcode)
        .filter(code => code && code.startsWith('L1100L23L'))
        .sort();
      
      if (sortedCodes.length > 0) {
        const lastCode = sortedCodes[sortedCodes.length - 1];
        const lastNumber = parseInt(lastCode.split('L').pop() || '0');
        newValuesetCode = `L1100L23L${lastNumber + 1}`;
      }
    }

    const payload = {
      orgCode: "TBD",
      orgName: "TBD",
      categoryCode: "L1100",
      categoryNameEn: "Appdata",
      categoryNameMl: "Appdata",
      parameterCode: "L1100L23",
      parameterNameEn: "Nada",
      parameterNameMl: "Nada",
      valuesetCode: newValuesetCode,
      valuesetNameEn: nadaName,
      valuesetNameMl: nadaName,
      valuesetOrder: maxOrder,
      isactive: "Y",
      cretedby: user?.userid || "SYSTEM"
    };

    return payload;
  };


  

  const columns: TableColumn[] = [
    {
      key: 'sNo',
      label: 'S.No.',
      sortable: true,
      width: 50,
      render: (_value: any, _row: any, rowIndex?: number, pagination?: any) => {
        const start = ((pagination?.page || 1) - 1) * (pagination?.limit || 0);
        return String((start + (rowIndex ?? 0) + 1)).padStart(2, '0');
      }
    },
    {
      key: 'valuesetnameen',
      label: 'Nada Name',
      sortable: true,
      width: 200,
      render: (value: string, row: any) => value || row?.valuesetnameml || row?.valuesetcode || '-'
    },
    {
      key: 'cretedby',
      label: 'Created By',
      sortable: true,
      width: 80,
      render: (value: string) => value || '-'
    },
    {
      key: 'createddt',
      label: 'Creation Date/Time',
      sortable: true,
      width: 160,
      render: (value: string) => formatDateTime(value)
    },
    {
      key: 'edit',
      label: 'Edit',
      sortable: false,
      width: 50,
      isEditColumn: true,
      onEditClick: handleEditClick,
    }
  ];

  // Format date and time for display
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const formattedDate = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      });
      const formattedTime = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${formattedDate} / ${formattedTime}`;
    } catch (error) {
      return '-';
    }
  };

  const pagination: SimplePaginationInfo = {
    page: currentPage,
    limit: 10,
  };

  return (
    <div className="h-100">
      <div className="row h-100">
        {/* Left Pane - Form (45%) */}
        <div className="col-lg-5 col-md-6">
          <div className="card h-100" style={{ background: '#e9e6ec' }}>
            <div className="card-header bg-primary text-secondary">
              <h6 className="mb-0">ADD NADA</h6>
            </div>
            <div className="card-body d-flex flex-column h-100">
              <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column h-100">
                {/* Form Content - Flexible */}
                <div className="">
                  {/* Nada Name Field */}
                  <div className="mb-4">
                    <label className="form-label text-primary">NADA NAME *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      placeholder="Enter Name of Nada"
                      {...register('name')}
                    />
                    {errors.name && (
                      <div className="invalid-feedback">{errors.name.message}</div>
                    )}
                  </div>
                </div>

                {/* Action Buttons - Fixed at Bottom */}
                <div className="d-flex justify-content-end">
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                    >
                      {isEditMode ? 'EDIT NADA' : 'ADD NADA'}
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                    >
                      CLEAR
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right Pane - Table (55%) */}
        <div className="col-lg-7 col-md-6">
          <div className="card h-100" style={{ background: '#e9e6ec' }}>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={nadaList}
                loading={loading}
                pagination={pagination}
                onPageChange={setCurrentPage}
                onSort={() => { }}
                searchable={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNada;